import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import {
  FileText,
  Download,
  Loader2,
  Filter,
  RefreshCw,
  FileSignature,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const API = `${(process.env.REACT_APP_BACKEND_URL || window.location.origin)}/api`;

const docTypeConfig = {
  quote: { label: "Devis", color: "bg-[#3B82F6]/10 text-[#3B82F6]" },
  contract: { label: "Contrat", color: "bg-[#0099CC]/10 text-[#0099CC]" },
  contract_signed: { label: "Contrat signé", color: "bg-[#10B981]/10 text-[#10B981]" },
  annex: { label: "Annexe", color: "bg-[#F59E0B]/10 text-[#F59E0B]" },
  cgv: { label: "CGV", color: "bg-[#64748b]/10 text-[#94A3B8]" },
};

export default function DocumentsPage() {
  const { isAdmin, token } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterDocType, setFilterDocType] = useState("all");
  const [filterContract, setFilterContract] = useState("all");

  // Signing
  const [signDialogOpen, setSignDialogOpen] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signContractId, setSignContractId] = useState(null);
  const [signerName, setSignerName] = useState("");
  const [confirm, setConfirm] = useState(false);

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [docsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/documents`),
        axios.get(`${API}/contracts`),
      ]);
      setDocuments(docsRes.data);
      setContracts(contractsRes.data);
    } catch (err) {
      console.error("Error fetching documents/contracts", err);
      toast.error("Erreur lors du chargement des documents");
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    return documents.filter((d) => {
      if (filterDocType !== "all" && d.doc_type !== filterDocType) return false;
      if (filterContract !== "all" && d.contract_id !== filterContract) return false;
      return true;
    });
  }, [documents, filterDocType, filterContract]);

  const awaitingSignature = useMemo(() => {
    return contracts.filter((c) => c.status === "awaiting_signature");
  }, [contracts]);

  const openSignDialog = (contractId) => {
    setSignContractId(contractId);
    setSignerName("");
    setConfirm(false);
    setSignDialogOpen(true);
  };

  const submitSignature = async (e) => {
    e.preventDefault();
    if (!signContractId) return;
    if (!signerName.trim()) {
      toast.error("Veuillez renseigner votre nom");
      return;
    }
    if (!confirm) {
      toast.error("Veuillez confirmer la signature");
      return;
    }

    setSigning(true);
    try {
      await axios.post(`${API}/contracts/sign`, {
        contract_id: signContractId,
        signer_name: signerName,
        confirm: true,
      });
      toast.success("Contrat signé");
      setSignDialogOpen(false);
      await fetchAll();
    } catch (err) {
      console.error("Error signing contract", err);
      toast.error("Erreur lors de la signature");
    } finally {
      setSigning(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(`${API}/documents/${doc.id}/download`, {
        responseType: "blob",
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      const blobUrl = window.URL.createObjectURL(response.data);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = doc.filename || `${doc.doc_type}.pdf`;
      document.body.appendChild(a);
      a.click();

      if (a.parentNode) a.parentNode.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed", err);
      toast.error("Téléchargement impossible (authentification requise)");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="documents-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isAdmin ? "Documents Clients" : "Mes Documents"}
          </h1>
          <p className="text-[#94A3B8] mt-1">
            {documents.length} document{documents.length > 1 ? "s" : ""}
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            variant="outline"
            onClick={fetchAll}
            className="border-[#2A3441] text-[#94A3B8] hover:text-white"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Rafraîchir
          </Button>
        </div>
      </div>

      {/* Actions: Signature (client only) */}
      {!isAdmin && awaitingSignature.length > 0 && (
        <div className="bg-[#0099CC]/10 border border-[#0099CC]/30 rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-white font-semibold">Signature requise</p>
            <p className="text-[#94A3B8] text-sm">
              {awaitingSignature.length} contrat{awaitingSignature.length > 1 ? "s" : ""} en attente de signature.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {awaitingSignature.slice(0, 3).map((c) => (
              <Button
                key={c.id}
                className="bg-[#0099CC] hover:bg-[#0088B5] text-white"
                onClick={() => openSignDialog(c.id)}
              >
                <FileSignature className="h-4 w-4 mr-2" />
                Signer v{c.version}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="w-full md:w-[220px]">
          <Select value={filterDocType} onValueChange={setFilterDocType}>
            <SelectTrigger className="bg-[#151B2B] border-[#2A3441] text-white">
              <Filter className="h-4 w-4 mr-2 text-[#64748b]" />
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent className="bg-[#151B2B] border-[#2A3441]">
              <SelectItem value="all">Tous les types</SelectItem>
              {Object.keys(docTypeConfig).map((k) => (
                <SelectItem key={k} value={k}>
                  {docTypeConfig[k].label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="w-full md:w-[320px]">
          <Select value={filterContract} onValueChange={setFilterContract}>
            <SelectTrigger className="bg-[#151B2B] border-[#2A3441] text-white">
              <FileText className="h-4 w-4 mr-2 text-[#64748b]" />
              <SelectValue placeholder="Contrat" />
            </SelectTrigger>
            <SelectContent className="bg-[#151B2B] border-[#2A3441]">
              <SelectItem value="all">Tous les contrats</SelectItem>
              {contracts.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.id.slice(0, 8)}… (v{c.version})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#0099CC]" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 bg-[#151B2B] rounded-xl border border-[#2A3441]">
          <FileText className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">Aucun document</h3>
          <p className="text-[#94A3B8]">Vos documents apparaîtront ici</p>
        </div>
      ) : (
        <div className="bg-[#151B2B] rounded-xl border border-[#2A3441] overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-6 py-4 bg-[#0B0F19] text-[#94A3B8] text-sm font-medium">
            <div className="col-span-3">Type</div>
            <div className="col-span-5">Contrat</div>
            <div className="col-span-2">Version</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>

          <div className="divide-y divide-[#2A3441]">
            {filtered.map((d) => {
              const cfg = docTypeConfig[d.doc_type] || {
                label: d.doc_type,
                color: "bg-[#2A3441] text-[#94A3B8]",
              };

              return (
                <div
                  key={d.id}
                  className="grid grid-cols-1 md:grid-cols-12 gap-3 md:gap-4 px-6 py-4 hover:bg-[#0B0F19]/50"
                >
                  <div className="md:col-span-3 flex items-center gap-2">
                    <Badge className={`${cfg.color} border-0`}>{cfg.label}</Badge>
                  </div>

                  <div className="md:col-span-5 text-[#94A3B8] font-mono text-xs flex items-center">
                    {d.contract_id.slice(0, 10)}…
                  </div>

                  <div className="md:col-span-2 text-[#94A3B8] flex items-center">v{d.version}</div>

                  <div className="md:col-span-2 flex md:justify-end">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(d)}
                      className="border-[#2A3441] text-[#94A3B8] hover:text-white"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Télécharger
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Signature Dialog */}
      <Dialog open={signDialogOpen} onOpenChange={setSignDialogOpen}>
        <DialogContent className="bg-[#151B2B] border-[#2A3441]">
          <DialogHeader>
            <DialogTitle className="text-white">Signer le contrat</DialogTitle>
          </DialogHeader>
          <form onSubmit={submitSignature} className="space-y-4">
            <div className="space-y-2">
              <Label className="text-[#94A3B8]">Nom complet *</Label>
              <Input
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Ex: Jean Dupont"
                className="bg-[#0B0F19] border-[#2A3441] text-white"
              />
            </div>

            <label className="flex items-start gap-2 text-[#94A3B8] text-sm">
              <input
                type="checkbox"
                checked={confirm}
                onChange={(e) => setConfirm(e.target.checked)}
                className="mt-1"
              />
              <span>
                Je confirme signer électroniquement ce contrat et j’accepte les documents contractuels.
              </span>
            </label>

            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setSignDialogOpen(false)}
                className="flex-1 border-[#2A3441] text-[#94A3B8]"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                disabled={signing}
                className="flex-1 bg-[#0099CC] hover:bg-[#0088B5] text-white"
              >
                {signing ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <FileSignature className="h-4 w-4 mr-2" />
                )}
                Signer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
